import React, { useState, useMemo } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { bindActionCreators } from 'redux';
import clsx from 'clsx';
import * as actions from 'containers/App/actions';
import APModal from 'components/Modal/APModal';
import { authAccountWithIntegration } from 'utils/integrations';
import EmailImage from 'assets/images/envelope.svg';
import Cards from 'components/Cards';
import TypeCard from 'components/Cards/TypeCard';
import GmailImage from 'assets/images/gmail.svg';
import iCloudImage from 'assets/images/icloud.svg';
import MicrosoftImage from 'assets/images/outlook.png';

const EmailIntegration = () => {
  const userInfo = useSelector((state) => state.global.userInfo);
  const { integrations } = userInfo;

  const [type, setType] = useState('');
  const [connectModalOpen, setConnectModalOpen] = useState(false);
  const [confirmModalOpen, setConfirmModalOpen] = useState(false);
  const [errorModalOpen, setErrorModalOpen] = useState(false);
  const [errorModalMessage, setErrorModalMessage] = useState('');

  const integrationsNonVirtual = useMemo(() => integrations
    .filter(integration => {
      return integration.grantStatus === 'valid' && integration.provider !== 'virtual-calendar';
    }), [integrations]);

  const dispatch = useDispatch();
  const {
    deleteIntegrationAccount,
  } = bindActionCreators(actions, dispatch);

  const handleIntegrationConnect = () => {
    // Connect a new account.
    if (!integrationsNonVirtual || integrationsNonVirtual.length < 1) {
      setConnectModalOpen(true);
      return;
    }
    // Re-integration an existing account.
    const expiredIntegration = integrations.find(int => int.grantStatus === 'expired');
    if (expiredIntegration) {
      const { provider, email } = expiredIntegration;
      authAccountWithIntegration({ provider, loginHint: email }, setErrorModal);
    }
  };

  const setErrorModal = (message) => {
    setErrorModalMessage(message);
    setErrorModalOpen(true);
  };

  const errorModalCloseHandler = () => {
    setErrorModalOpen(false);
    setErrorModalMessage(null);
  };

  const getProviderImage = (provider) => {
    switch (provider) {
      case 'google':
        return GmailImage;
      case 'icloud':
        return iCloudImage;
      case 'microsoft':
        return MicrosoftImage;
      default:
        return false;
    }
  };

  return (
    <>
      <div className="integration">
        <div className="integration__image email">
          <img src={EmailImage} />
        </div>
        <div className="integration__content">
          <h5>EMAIL</h5>
          <p>
            Connect your Google, Apple, or Microsoft email to enable Aisle Planner’s lead and client
            email feature. Once connected, you’ll be able to create and send lead and client emails easily,
            as well as track them in Aisle Planner.
            <br />
            <a href="https://help.aisleplanner.com/en/articles/10306544-lead-emailing-and-integration"
              target="_blank"
              rel="noopener noreferrer"
            >
              Learn more
            </a>.
          </p>
          {integrationsNonVirtual.length ? (
            <div className={clsx('connected-provider', integrationsNonVirtual[0].provider)}>
              <img src={getProviderImage(integrationsNonVirtual[0].provider)} />
              <span>Your connected email: <strong>{integrationsNonVirtual[0].email}</strong></span>
              <a
                href="#"
                onClick={(e) => {
                  e.preventDefault();
                  setConfirmModalOpen(true);
                }}
              >
                Disconnect
              </a>
            </div>
          ) : null}
        </div>
        <div className="integration__actions">
          <button
            className={clsx('button', integrationsNonVirtual.length && 'button--connected')}
            onClick={handleIntegrationConnect}
          >
            {integrationsNonVirtual.length ? 'Connected' : 'Connect'}
          </button>
        </div>
      </div>

      <APModal
        open={connectModalOpen}
        title="Connect your email."
        subtitle="Select an external email provider"
        name="connect-provider"
        size="medium"
        onClose={() => setConnectModalOpen(false)}
      >
        <Cards>
          <TypeCard
            selected={type === 'google'}
            name="gmail"
            title="Gmail"
            img={{
              imgUrl: GmailImage,
              alt: 'Gmail',
            }}
            onClick={() => setType('google')}
          />
          <TypeCard
            selected={type === 'icloud'}
            name="icloud"
            title="Apple iCloud"
            img={{
              imgUrl: iCloudImage,
              alt: 'Apple iCloud',
            }}
            onClick={() => setType('icloud')}
          />
          <TypeCard
            selected={type === 'microsoft'}
            name="microsoft-365"
            title="Microsoft 365"
            img={{
              imgUrl: MicrosoftImage,
              alt: 'Microsoft 365',
            }}
            onClick={() => setType('microsoft')}
          />
        </Cards>
        <button
          className="button"
          onClick={() => authAccountWithIntegration(
            { 'provider': type, 'baseUrl': window.location.origin },
            setErrorModal,
          )}
          disabled={!type}
        >
          Continue
        </button>
      </APModal>

      <APModal
        open={errorModalOpen}
        onClose={errorModalCloseHandler}
        actions={(
          <button
            onClick={errorModalCloseHandler}
            className="button"
          >
            Ok
          </button>
        )}
      >
        <div>{errorModalMessage}</div>
      </APModal>

      <APModal
        title="Disconnect Your External Account"
        name="confirm-disconnect"
        open={confirmModalOpen}
        onClose={() => setConfirmModalOpen(false)}
        actions={(
          <>
            <button
              onClick={() => setConfirmModalOpen(false)}
              className="button button--cancel"
            >
              Cancel
            </button>
            <button
              onClick={() => {
                deleteIntegrationAccount(integrationsNonVirtual[0].id);
                setConfirmModalOpen(false);
                window.location.reload();
              }}
              className="button"
            >
              Disconnect
            </button>
          </>
        )}
      >
        <p>
          Your scheduling, calendar and email integrations are all enabled by the external account you linked to Aisle
          Planner. By disconnecting, any features enabled by this integration will no longer be active. Are you sure you
          want to disconnect?
        </p>
      </APModal>
    </>
  );
};

export default EmailIntegration;
